import api from './api';

export const LNS_UDP_HOST = process.env.REACT_APP_LNS_HOST || 'lns-dev.aurex.nolim.app';
export const LNS_UDP_PORT = process.env.REACT_APP_LNS_PORT || '1700';

export const GATEWAY_MODELS = [
  'Milesight UG65',
  'Milesight UG67',
  'Seeed SenseCAP M2 Indoor (SX1302)',
  'Seeed SenseCAP M2 Indoor (SX1302-4G)',
  'Seeed SenseCAP M2 Outdoor',
  'Dragino LPS8v2-EC25',
] as const;

export type GatewayModel = (typeof GATEWAY_MODELS)[number];

export interface GatewayCreatePayload {
  name: string;
  eui: string;
  model: GatewayModel;
  freq_band?: 'EU868';
  user_id: number;
  object_id?: number | null;
}

export interface GatewayUpdatePayload {
  name: string;
  user_id: number;
  object_id: number | null;
}

export interface GatewayResponse {
  id: number;
  name: string;
  eui: string;
  model: string;
  freq_band: string;
  user_id: number;
  object_id: number | null;
  last_seen_at: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface GatewayConflictDetail {
  detail: string;
  field: string;
  value: string;
}

export interface UserOption {
  id: number;
  fullname: string;
}

export interface GatewayFilterUser {
  id: number;
  fullname: string;
}

export interface GatewayFilterObject {
  id: number;
  name: string;
  user_id: number;
}

export interface ObjectOption {
  id: number;
  name: string;
  user_id: number;
}

export interface RecognizeEuiResponse {
  recognized: boolean;
  eui: string | null;
  message: string | null;
}

export interface GatewayListItem {
  id: number;
  name: string;
  eui: string;
  model: string;
  freq_band: string;
  user_id: number;
  user_fullname: string | null;
  object_id: number | null;
  object_name: string | null;
  status: 'online' | 'offline' | 'never_seen';
  last_seen_at: string | null;
  devices_count: number;
}

export interface GatewayListResponse {
  items: GatewayListItem[];
  total: number;
}

export const GATEWAY_EUI_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const GATEWAY_EUI_MAX_SIZE = 5 * 1024 * 1024;

const skipRedirects = { skipRedirects: true } as any;

export const gatewaysApi = {
  list: async (): Promise<GatewayListResponse> => {
    const response = await api.get<GatewayListResponse>('/api/v1/admin/gateways');
    return response.data;
  },

  create: async (payload: GatewayCreatePayload): Promise<GatewayResponse> => {
    const response = await api.post<GatewayResponse>(
      '/api/v1/admin/gateways',
      payload,
      skipRedirects,
    );
    return response.data;
  },

  update: async (id: number, payload: GatewayUpdatePayload): Promise<GatewayResponse> => {
    const response = await api.patch<GatewayResponse>(
      `/api/v1/admin/gateways/${id}`,
      payload,
      skipRedirects,
    );
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`/api/v1/admin/gateways/${id}`, skipRedirects);
  },

  listFilterUsers: async (): Promise<GatewayFilterUser[]> => {
    const response = await api.get<{ items: GatewayFilterUser[] }>(
      '/api/v1/admin/gateways/filter-users',
    );
    return response.data.items || [];
  },

  listFilterObjects: async (): Promise<GatewayFilterObject[]> => {
    const response = await api.get<{ items: GatewayFilterObject[] }>(
      '/api/v1/admin/gateways/filter-objects',
    );
    return response.data.items || [];
  },

  listUsers: async (): Promise<UserOption[]> => {
    const response = await api.get<{
      items: Array<{ id: number; fullname?: string | null; email: string; role: string }>;
    }>('/api/v1/users?page=1&limit=1000');
    return (response.data.items || [])
      .filter((u) => u.role === 'user')
      .map((u) => ({ id: u.id, fullname: u.fullname || u.email }));
  },

  recognizeEui: async (file: File): Promise<RecognizeEuiResponse> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post<RecognizeEuiResponse>(
      '/api/v1/admin/gateways/recognize-eui',
      form,
      { ...skipRedirects, headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  listObjects: async (userId?: number): Promise<ObjectOption[]> => {
    const url = userId
      ? `/api/v1/users/${userId}/objects`
      : '/api/v1/objects?limit=1000';
    const response = await api.get<Array<{ id: number; name: string; user_id: number; is_deleted: boolean }>>(url);
    return (response.data || [])
      .filter((o) => !o.is_deleted)
      .map((o) => ({ id: o.id, name: o.name, user_id: o.user_id }));
  },
};
